// Camera-backed QR scanner used by the offline pairing flow.
//
// Wraps `qr-scanner` (Nimiq) — gives us a tight component that turns
// the rear camera into a single-shot decoder. `onDecoded` fires once;
// the scanner is torn down immediately afterwards so the camera light
// goes off.

import { useEffect, useRef, useState } from 'react'
import QrScannerLib from 'qr-scanner'

export interface QrScannerProps {
  onDecoded: (text: string) => void
  onCancel: () => void
  prompt: string
}

export function QrScanner({ onDecoded, onCancel, prompt }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const scannerRef = useRef<QrScannerLib | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    let cancelled = false

    const scanner = new QrScannerLib(
      video,
      (result) => {
        if (cancelled) return
        cancelled = true
        try { scanner.stop() } catch { /* noop */ }
        onDecoded(result.data)
      },
      {
        preferredCamera: 'environment',
        highlightScanRegion: true,
        highlightCodeOutline: true,
        maxScansPerSecond: 5,
      },
    )
    scannerRef.current = scanner
    scanner.start().catch((e) => {
      setError(e instanceof Error ? e.message : String(e))
    })

    return () => {
      cancelled = true
      try { scanner.stop() } catch { /* noop */ }
      try { scanner.destroy() } catch { /* noop */ }
      scannerRef.current = null
    }
  }, [onDecoded])

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/95">
      <div className="flex items-center justify-between px-4 py-3 text-sm text-zinc-200">
        <span className="font-medium">{prompt}</span>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-100 ring-1 ring-zinc-700"
        >
          Cancel
        </button>
      </div>
      <div className="relative flex-1">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          playsInline
          muted
        />
        {error && (
          <div className="absolute inset-x-0 bottom-0 bg-red-900/80 px-4 py-3 text-sm text-white">
            Camera error: {error}
            <div className="mt-1 text-xs text-red-200">
              Grant camera permission in your browser settings and try again.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
